import { Permission } from "@rumble-it/common-lib/types/models/Permission.interface";
import { Input } from "antd";
import { useState } from "react";
import type { ColumnsType, TableRowSelection } from "antd/es/table/interface";
import { useQuery } from "@tanstack/react-query";
import { fetchPermissions } from "../../../../lib/api/resources/roles";
import { Table } from "@/components/v2";
import { Card } from "@rumbledirect/ui";

const { Search } = Input;

interface RolePermissionsCardProps {
  permissions?: string[];
  isLoading?: boolean;
  editMode?: boolean;
  selectedPermissionIds?: string[];
  setSelectedPermissionIds?: (permissionIds: string[]) => void;
  onPermissionsChange?: (permissionIds: string[]) => void;
}

export const RolePermissionsCard = ({
  permissions = [],
  isLoading,
  editMode = false,
  selectedPermissionIds = [],
  setSelectedPermissionIds,
}: RolePermissionsCardProps) => {
  const [searchTerm, setSearchTerm] = useState("");

  const permissionsQuery = useQuery({
    queryKey: ["permissions"],
    queryFn: fetchPermissions,
  });

  const allPermissions = permissionsQuery.data || [];

  const dataSource = editMode
    ? allPermissions
    : allPermissions.filter((p) => permissions.includes(p.id));

  const filteredPermissions = dataSource.filter(
    (permission) =>
      permission.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      permission.description?.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  const columns: ColumnsType<Permission> = [
    {
      title: "Naam",
      dataIndex: "name",
      key: "name",
      sorter: (a, b) => a.name.localeCompare(b.name),
    },
    {
      title: "Omschrijving",
      dataIndex: "description",
      key: "description",
    },
  ];

  const rowSelection: TableRowSelection<Permission> | undefined = editMode
    ? {
        selectedRowKeys: selectedPermissionIds,
        preserveSelectedRowKeys: true,
        onChange: (selectedRowKeys) => {
          const newSelection = selectedRowKeys as string[];
          newSelection;
          setSelectedPermissionIds?.(newSelection);
        },
      }
    : undefined;

  return (
    <Card
      loading={isLoading || (permissionsQuery.isLoading && editMode)}
      className="w-full"
      title={`Permissies${editMode ? ` (${selectedPermissionIds.length} geselecteerd)` : ""}`}
    >
      <Search
        placeholder="Zoek permissies op naam of omschrijving..."
        allowClear
        onChange={(e) => setSearchTerm(e.target.value)}
        style={{ marginBottom: 16 }}
      />

      <Table
        size="small"
        columns={columns}
        dataSource={filteredPermissions}
        rowKey={(record) => record["id"]}
        rowSelection={rowSelection}
        pagination={false}
        scroll={{ y: 500 }}
      />
    </Card>
  );
};
